import { Modal, generateResolver, yup, Notification, STATUS_TYPES, ToggableDropdown, USER_PERMISSIONS } from 'dyl-components';
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Icon, Popup, Dropdown } from 'semantic-ui-react';
import contactsActions from 'actions/contacts';
import { BulkActionsContext } from "shared/context/BulkActionsProvider";
import { useSearchParams } from 'react-router-dom';
import groupsActions from 'actions/groups';
import campaignsActions from 'actions/campaigns';
import UserSearchField from "shared/forms/UserSearchField";
import FullScreenModalPopUp from 'shared/FullScreenModalPopUp';
import RestrictedView from 'shared/RestrictedView';


const ContactBulkActionsModal = ({ open, onClose, numberSelectedContacts = 0, module, refresh }) => {
    const [groupOptions, setGroupOptions] = useState([]);
    const [groupsSearch, setGroupsSearch] = useState('');
    const [query] = useSearchParams();
    const searchParam = query.get("search") || '';
    const account_type = query.get("account_type") || '';
    const start_time = query.get("start") || '';
    const end_time = query.get("end") || '';
    const outcome = query.get("outcome") || '';
    const CAMPAIGN_LIMIT = 999;
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false)

    const { isCreatingBulkAction } = useSelector((state) => state.contacts);
    const { control, reset, formState: { isValid, isDirty }, handleSubmit, watch, setValue } = useForm({
        mode: 'onChange',
        defaultValues: {
            type: '',
            group: '',
            owned_by: 'unassigned',
            co_owned_by: 'unassigned',
        },
        resolver: generateResolver({
            type: yup.string().required('This field is required'),
            group: yup.mixed().when('type', {
                is: 'move_contacts',
                then: schema => schema.required('This field is required')
            }),
            owned_by: yup.mixed().when('type', {
                is: 'add_owner',
                then: (schema) =>
                    schema
                        .test(
                            'not-empty',
                            'This field is required',
                            (value) => value !== '' && value !== undefined && value !== null
                        )
                        .required('This field is required'),
                otherwise: (schema) => schema.notRequired(),
            }),

            co_owned_by: yup.mixed().when('type', {
                is: 'add_coowner',
                then: (schema) =>
                    schema
                        .test(
                            'not-empty',
                            'This field is required',
                            (value) => value !== '' && value !== undefined && value !== null
                        )
                        .required('This field is required'),
                otherwise: (schema) => schema.notRequired(),
            }),
        })
    });
    const { groups, subgroups, isReadingGroups, campaigns, isReadingCampaigns } = useSelector((state) => {
        return {
            groups: state.groups.groups,
            subgroups: state.groups.subgroups,
            isReadingGroups: state.groups.isReadingGroups,
            campaigns: state.campaigns.campaigns,
            isReadingCampaigns: state.campaigns.isReadingCampaigns
        }
    });

    const dispatch = useDispatch();

    const typeWatch = watch('type');

    const close = () => {
        reset();
        onClose();
    }

    const [selectedContacts, setSelectedContacts, areContactsInAllPagesSelected, setAreContactsInAllPagesSelected] = useContext(BulkActionsContext);
    const bulkActionOptions = [
        {
            key: 'add_to_hotlist', value: 'add_to_hotlist', text: 'Add to Hotlist', userPermission: USER_PERMISSIONS.CAMPAIGNS_EDIT, content: (
                <>
                    <Icon name='hotjar' />
                    Add to Hotlist
                </>
            )
        },
        {
            key: 'add_to_campaign', value: 'add_to_campaign', text: 'Add to Campaign', userPermission: USER_PERMISSIONS.CAMPAIGNS_EDIT, content: (
                <>
                    <Icon className={`fas fa-megaphone`} />
                    Add to Campaign
                </>
            )
        },
        {
            key: 'add_to_contact_group', value: 'add_to_label', text: 'Add to Contact Group', userPermission: USER_PERMISSIONS.GROUPS_SUBGROUPS_EDIT, content: (
                <>
                    <Icon className={`fas fa-folder`} />
                    Add to Contact Group
                </>
            ),
        },
        {
            key: "add_owner",
            value: "add_owner",
            text: "Add Owner",
            userPermission: USER_PERMISSIONS.OWNERSHIP_ASSIGNMENT_ADD, 
            content: (
                <>
                    <Icon className={`fas fa-user-check`} />
                    Add Owner
                </>
            ),
        },
        {
            key: "add_coowner",
            value: "add_coowner",
            text: "Add Co-owner",
            userPermission: USER_PERMISSIONS.OWNERSHIP_ASSIGNMENT_ADD, 
            content: (
                <>
                    <Icon className={`fas fa-user-check`} />
                    Add Co-owner
                </>
            ),
        },
        {
            key: "set_direct_customer",
            value: "set_direct_customer",
            text: "Mark as Direct Customer",
            content: (
                <>
                    <Icon className='fas fa-crown' />
                    Mark as Direct Customer
                </>
            ),
        },
        {
            key: "unset_direct_customer",
            value: "unset_direct_customer",
            text: "Unmark as Direct Customer",
            content: (
                <>
                    <Icon className='fas fa-crown' />
                    Unmark as Direct Customer
                </>
            ),
        }
    ];

    const getMessages = (type) => {
        switch (type) {
            case "add_to_hotlist":
                return {
                    successMessage: "Successfully added to Hotlist!",
                    errorMessage: "Failed to add to Hotlist"
                }
            case "add_to_label":
                return {
                    successMessage: "Successfully added to group!",
                    errorMessage: "Failed to add to group"
                }
            case "add_to_campaign":
                return {
                    successMessage: "Successfully added to campaign!",
                    errorMessage: "Failed to add to campaign"
                }
            case "add_owner":
                return {
                    successMessage: "Successfully assigned owners to contacts!",
                    successMessageForUnassigned: "Successfully unassigned owners from selected contacts!",
                    errorMessage: "Failed to assign owners to contacts!",
                    errorMessageForUnassigned: "Failed to unassign owners from selected contacts!"
                }
            case "add_coowner":
                return {
                    successMessage: "Successfully assigned co-owners to contacts!",
                    successMessageForUnassigned: "Successfully unassigned co-owners from selected contacts!",
                    errorMessage: "Failed to assign co-owners to contacts!",
                    errorMessageForUnassigned: "Failed to unassign co-owners from selected contacts!"
                }
            case "set_direct_customer":
                return {
                    successMessage: "Successfully marked as direct customer!",
                    errorMessage: "Failed to mark as direct customer",
                };
            case "unset_direct_customer":
                return {
                    successMessage: "Successfully unmarked as direct customer!",
                    errorMessage: "Failed to unmark as direct customer",
                };
            default: { }
        }
    }
    const userOptions = useSelector(state => state.users.userOptions);

    const onBulkAction = async ({ type, group, campaign, owned_by, co_owned_by }) => {
        const { successMessage, successMessageForUnassigned = undefined, errorMessage, errorMessageForUnassigned = undefined } = getMessages(type);
        try {
            const contact_id = areContactsInAllPagesSelected
                ? []
                : [...new Set(selectedContacts)];

            const owner_id =
                type === "add_owner" ? owned_by : co_owned_by;

            const owner = userOptions.find(
                ({ user_id }) => Number(user_id) === Number(owner_id)
            );

            const payload = (() => {
                if (type === "add_to_hotlist" || type === "add_to_label") {
                    return {
                        new_label_id: group?.value || null
                    };
                }

                if (type === "add_to_campaign") {
                    return {
                        campaign_id: campaign
                    };
                }

                if (type.includes("owner")) {
                    const isUnassigned = owner_id === "unassigned";

                    return {
                        owner_id: isUnassigned ? null : owner_id,
                        owner_name: isUnassigned
                            ? undefined
                            : `${owner?.first_name} ${owner?.last_name}`
                    };
                }

                return {};
            })();

            await dispatch(
                contactsActions.contactBulkAction(
                    {
                        action: type,
                        contact_id,
                        ...payload
                    },
                    {
                        search: searchParam,
                        account_type,
                        start_time,
                        end_time,
                        outcome,
                        module
                    }
                )
            );

            setSelectedContacts([]);
            setAreContactsInAllPagesSelected(false);

            refresh && refresh();
            close();

            Notification.alert(
                (type === "add_owner" && owned_by === "unassigned") ||
                    (type === "add_coowner" && co_owned_by === "unassigned")
                    ? successMessageForUnassigned
                    : successMessage,
                STATUS_TYPES.SUCCESS
            );
        } catch (error) {
            console.log(error);
            Notification.alert((type === "add_owner" && owned_by === "unassigned") || (type === "add_coowner" && co_owned_by === "unassigned") ? errorMessageForUnassigned : errorMessage, STATUS_TYPES.ERROR);
        }
    }

    const handleSearchChange = (e, { searchQuery }) => {
        setGroupsSearch(searchQuery);
    }

    const getGroups = useCallback(async (search) => {
        await dispatch(groupsActions.readGroups({ search, limit: 500 }));
    }, [dispatch])

    const getSubgroups = async (parent_label_id) => {
        await dispatch(groupsActions.readSubgroups({ parent_label_id }));
    }

    const getCampaigns = useCallback(async () => {
        dispatch(campaignsActions.readCampaigns({ limit: CAMPAIGN_LIMIT, status: "active" }));
    }, [dispatch])

    useEffect(() => {
        getGroups(groupsSearch);
    }, [groupsSearch, getGroups])

    useEffect(() => {
        getCampaigns();
    }, [getCampaigns])

    useEffect(() => {
        const groupOptionsAux = groups.map((group) => {
            let subgroupsAux = [];
            if (subgroups[group.id] && subgroups[group.id].length > 0) {
                subgroupsAux = subgroups[group.id].map((subgroup) => ({ text: subgroup.name, key: subgroup.id, value: subgroup.id }));
            }
            return { text: group.name, key: group.id, value: group.id, hasOptions: group.sub_labels, options: subgroupsAux, parent: group.parent_label_name || null };
        })
        setGroupOptions(groupOptionsAux);
    }, [groups, subgroups])

    return (
        <Modal open={open} onClose={close}>
            <Modal.Header>
                Bulk Action <div style={{ float: 'right', marginRight: '1em' }}>
                    <small>{numberSelectedContacts} selected</small>
                    <Popup
                        trigger={<Icon size='small' style={{ marginLeft: '1em' }} floated='right' className='fas fa-circle-info' color='primary' />}
                        content={`Duplicates will be ignored`}
                        inverted
                        position='left center'
                        wide
                    />
                </div>
            </Modal.Header>
            <Modal.Content>
                <Form noValidate>
                    <Controller
                        name='type'
                        control={control}
                        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                            <Form.Select
                                {...value ? { text: bulkActionOptions.find(action => action.value === value)?.content } : {}}
                                label='Bulk Action Type'
                                placeholder='Select bulk action type'
                                text={bulkActionOptions.find(item => item.value === value)?.content}
                                value={value}
                                options={bulkActionOptions}
                                required
                                error={error?.message}
                                width={8}
                                selectOnBlur={false}
                            >
                                <Dropdown.Menu>
                                    {bulkActionOptions.map(item => (
                                        <RestrictedView type="disabled" userPermission={item.userPermission}>
                                            <Dropdown.Item key={item.key} value={item.value} text={item.content} active={value === item.value} selected={value === item.value} onClick={() => {
                                                    onChange(item.value);
                                                }} 
                                            />
                                        </RestrictedView>
                                    ))}
                                </Dropdown.Menu>
                            </Form.Select>
                        )}
                    />
                    {typeWatch === "add_to_label" && (
                        <Controller
                            name='group'
                            control={control}
                            render={({ field: { name, value, onChange } }) => (
                                <Form.Field
                                    control={ToggableDropdown}
                                    value={value}
                                    nested_options={groupOptions}
                                    onChange={(_, { value }) => {
                                        onChange({ target: { name, value } });
                                    }}
                                    placeholder='Search Group Name'
                                    label='Group/Subgroup Name'
                                    onHoverParent={getSubgroups}
                                    loading={isReadingGroups}
                                    onSearchChange={handleSearchChange}
                                    searchValue={groupsSearch}
                                    resetSearchValue={() => setGroupsSearch("")}
                                    width={8}
                                    selection
                                    required
                                />
                            )}
                        />
                    )}
                    {typeWatch === "add_to_campaign" && (
                        <Controller
                            name='campaign'
                            control={control}
                            render={({ field: { name, value, onChange } }) => (
                                <Form.Select
                                    value={value}
                                    options={campaigns.filter((campaign) => !campaign.deleted).map((campaign) => ({ key: campaign.id, text: campaign.name, value: campaign.id }))}
                                    onChange={(_, { value }) => {
                                        onChange({ target: { name, value } });
                                    }}
                                    placeholder={
                                        <div>
                                            <Icon
                                                style={{ marginRight: 10 }}
                                                name="search"
                                            />
                                            Select an Existing Campaign Name
                                        </div>
                                    }
                                    label='Campaign Name'
                                    loading={isReadingCampaigns}
                                    search
                                    width={15}
                                    selection
                                    required
                                    selectOnBlur={false}
                                />
                            )}
                        />
                    )}

                    {typeWatch === "add_owner" && (
                        <div
                            className="p-4 border rounded-2xl bg-gray-50 mt-4"
                        >
                            <Form>
                                <Form.Group widths="equal" style={{ marginBottom: "1rem" }}>
                                    <Form.Field width={8}>
                                        <label className="font-semibold mb-2 block">Owned By</label>
                                        <Controller
                                            name="owned_by"
                                            control={control}
                                            render={({ field: { name, value, onChange } }) => (
                                                <Form.Field
                                                    name={name}
                                                    value={value}
                                                    ownedOrCoOwned="owned_by"
                                                    control={UserSearchField}
                                                    placeholder="Search owner"
                                                    isUnassignable
                                                    clearable
                                                    display_selected_user
                                                    excluded_users={[value]}
                                                    onBlur
                                                    onChange={(_, { value }) => {
                                                        onChange({ target: { name, value } })
                                                    }}
                                                    onRemoveOneUser={() => {
                                                        setValue("owned_by", "")
                                                    }}
                                                    selected={watch("owned_by") === null ? [] : [watch("owned_by")]}
                                                    one_user_display
                                                />
                                            )}
                                        />
                                    </Form.Field>

                                </Form.Group>
                            </Form>
                        </div>
                    )}
                    {typeWatch === "add_coowner" && (
                        <div
                            className="p-4 border rounded-2xl bg-gray-50 mt-4"
                        >
                            <Form>
                                <Form.Group widths="equal" style={{ marginBottom: "1rem" }}>
                                    <Form.Field width={8}>
                                        <label className="font-semibold mb-2 block">Co-Owned By</label>
                                        <Controller
                                            name="co_owned_by"
                                            control={control}
                                            render={({ field: { name, value, onChange } }) => (
                                                <Form.Field
                                                    name={name}
                                                    value={value}
                                                    ownedOrCoOwned="co_owned_by"
                                                    control={UserSearchField}
                                                    isUnassignable
                                                    placeholder="Search co-owner"
                                                    clearable
                                                    display_selected_user
                                                    excluded_users={[value]}
                                                    onBlur
                                                    onChange={(_, { value }) => {
                                                        onChange({ target: { name, value } })
                                                    }}
                                                    onRemoveOneUser={() => {
                                                        setValue("co_owned_by", "")
                                                    }}
                                                    selected={watch("co_owned_by") === null ? [] : [watch("co_owned_by")]}
                                                    one_user_display
                                                />
                                            )}
                                        />
                                    </Form.Field>
                                </Form.Group>
                            </Form>
                        </div>
                    )}
                </Form>
            </Modal.Content>
            <Modal.Actions
                hasSaveButton
                saveOptions={{
                    loading: isCreatingBulkAction
                }}
                saveDisabled={!isValid || !isDirty || isCreatingBulkAction}
                onSave={() => {

                    if (typeWatch === "add_owner" || typeWatch === "add_coowner") {
                        setIsConfirmationOpen(true)
                    } else {
                        handleSubmit(onBulkAction)();
                    }
                }
                }
            />
            <FullScreenModalPopUp
                header={"Are you sure?"}
                subheader={`Existing ${typeWatch === "add_owner" ? "owners" : "co-owners"} will be replaced.`}
                isOpen={isConfirmationOpen}
                onConfirm={() => {
                    handleSubmit(onBulkAction)();
                    setIsConfirmationOpen(false);
                }}
                onFormClose={() => setIsConfirmationOpen(false)}
                closeIcon={false}
            />
        </Modal>
    )
}

export default ContactBulkActionsModal;
